import config from '@config'
import useIsInScribbleContent from '@hooks/useIsInScribbleContent'
import { useVideo } from '@hooks/useVideo'
import useVideoSkeleton from '@hooks/useVideoSkeleton'
import { useEffect, useRef } from 'react'
import {
  UseCommonPropsInputProps,
  UseCommonPropsOutputProps,
} from '../types/darkcove'
import {
  getAdSkipTime,
  getContextVideoType,
  getPlayerType,
  getPosterImageUrl,
  getVideoTypeParam,
} from '../utils'
import useTracking from './tracking/useTracking'

const {
  backend: { imageUrl },
  ads: {
    adSkipTimeDurationInSeconds: adSkipTimeDurationInSecondsDefault,
    videoTypeThresholdInSeconds: videoTypeThresholdInSecondsDefault,
    adMidrollThresholdInSeconds: adMidrollThresholdInSecondsDefault,
    adMidrollOffsetInPercent: adMidrollOffsetInPercentDefault,
  },
  video: { accountId, logoUrlBlickTV },
} = config

const useCommonProps = ({
  playerRef,
  isBlickTV,
  allowFloating,
  autoplay,
  videoId,
  title,
  position,
  allowVideoCropping,
  image,
  duration,
  isAdDisabled,
  switchHeadline,
  isInVideoOnlyArticle,
  isInViewport,
}: UseCommonPropsInputProps): UseCommonPropsOutputProps => {
  const wasImpressionTracked = useRef<boolean>(false)
  const { addVideo } = useVideo()
  const isInScribble = !!useIsInScribbleContent()

  const {
    adSkipTimeDurationInSeconds = adSkipTimeDurationInSecondsDefault,
    videoTypeThresholdInSeconds = videoTypeThresholdInSecondsDefault,
    adMidrollThresholdInSeconds = adMidrollThresholdInSecondsDefault,
    adMidrollOffsetInPercent = adMidrollOffsetInPercentDefault,
  } = useVideoSkeleton()

  const adsEnabled = !isAdDisabled

  const vtype = getVideoTypeParam({
    duration,
    shortVideoLengthThresholdInSeconds: videoTypeThresholdInSeconds,
  })

  const skipAd = getAdSkipTime(vtype, adSkipTimeDurationInSeconds)
  const contextVideoType = getContextVideoType({ isBlickTV, allowFloating })
  const playerType = getPlayerType({ isBlickTV, duration })
  const posterURL = getPosterImageUrl(imageUrl, image)

  const trackingCallbacks = useTracking({
    isBlickTV,
    allowFloating,
    position,
    autoplay,
    playerRef,
    playerType,
    videoId,
    title,
    duration,
    isInVideoOnlyArticle,
    vtype,
  })

  const naturalOverlayAspectRatio = !allowVideoCropping

  useEffect(() => {
    if (isInViewport && !wasImpressionTracked.current) {
      trackingCallbacks?.onImpression?.()
      wasImpressionTracked.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInViewport])

  return {
    isBlickTV,
    allowFloating,
    showCaptionsInline: true,
    showCaptionsOverlay: true,
    showControlsInline: true,
    showControlsOverlay: true,
    showGraphicsInline: true,
    showGraphicsOverlay: true,
    accountId,
    videoId,
    posterURL,
    adsEnabled,
    vtype,
    skipAd,
    logoURL: logoUrlBlickTV,
    adSkipTimeDurationInSeconds,
    adMidrollThresholdInSeconds,
    adMidrollOffsetInPercent,
    inlineFlag: true,
    naturalOverlayAspectRatio,
    isInVideoOnlyArticle,
    isInScribble,

    trackingCallbacks: {
      ...trackingCallbacks,

      onHeadlineChange: (e: any) => {
        if (isBlickTV && e?.headline) {
          switchHeadline?.(e.headline)
          trackingCallbacks?.setNewHeadline?.(e.headline)
        }
      },

      onPlay: () => {
        addVideo({
          id: videoId,
          ref: playerRef,
          type: contextVideoType,
        })
        trackingCallbacks?.onPlay?.()
      },

      onPlaySuccess: () => {
        addVideo({
          id: videoId,
          ref: playerRef,
          type: contextVideoType,
        })
        trackingCallbacks?.onPlaySuccess?.()
      },

      onAdStarted: () => {
        addVideo({
          id: videoId,
          ref: playerRef,
          type: contextVideoType,
        })
        trackingCallbacks?.onAdStarted?.()
      },

      onAdResumed: () => {
        addVideo({
          id: videoId,
          ref: playerRef,
          type: contextVideoType,
        })
      },
    },
  }
}

export default useCommonProps
